import { useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const isCentralSalesSource = (s) => (s || "").toLowerCase().includes("central sales");

// "Hand off to Central Sales": a studio owner/manager assigns a lead (customer) or job to a
// Central Sales Team B2C member for assisted closing. The member becomes the primary handler
// (queue + notification); the studio keeps full visibility and edit access. Once handed off,
// only an admin can reassign it. kind is "job" or "lead".
export const HandoffButton = ({ user, record, kind, onDone }) => {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [sel, setSel] = useState("");
  const [saving, setSaving] = useState(false);

  const isAdmin = ["network_admin", "ceo"].includes(user.role);
  const canHandoff = ["studio_owner", "studio_manager", "network_admin", "ceo"].includes(user.role);
  const alreadyHandedOff = !!record?.cs_person_id;
  const show =
    canHandoff &&
    !!record?.studio_id &&
    !isCentralSalesSource(record?.lead_source) &&
    (!alreadyHandedOff || isAdmin);

  useEffect(() => {
    if (open && members.length === 0) {
      api
        .get("/users", { params: { role: "central_sales_team" } })
        .then(({ data }) => setMembers((data || []).filter((m) => m.active !== false)))
        .catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!show) return null;

  const submit = async () => {
    if (!sel) return toast.error("Select a Central Sales Team member");
    setSaving(true);
    try {
      const url = kind === "job" ? `/jobs/${record.id}/handoff` : `/customers/${record.id}/handoff`;
      const { data } = await api.post(url, { cs_person_id: sel });
      toast.success(alreadyHandedOff ? "Reassigned to Central Sales" : "Handed off to Central Sales");
      setOpen(false);
      setSel("");
      onDone?.(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="min-h-[44px] border-brand-ink/20"
          data-testid={`handoff-${kind}-btn`}
        >
          <UserPlus className="mr-2 h-4 w-4" />
          {alreadyHandedOff ? "Reassign Central Sales" : "Hand off to Central Sales"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`handoff-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle>
            {alreadyHandedOff ? "Reassign to another" : "Hand off to"} Central Sales Team B2C
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            The selected member becomes the primary handler for assisted closing and gets this in
            their queue with a notification. You keep full visibility and can still edit.
          </p>
          {record.cs_person_name && (
            <p className="text-xs text-slate-500">
              Currently with: <b>{record.cs_person_name}</b>
            </p>
          )}
          <div>
            <Label className="text-xs">Central Sales Team member</Label>
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger className="mt-1" data-testid="handoff-member-select">
                <SelectValue placeholder="Select a member" />
              </SelectTrigger>
              <SelectContent>
                {members.length === 0 && (
                  <div className="px-2 py-1.5 text-sm text-slate-500">No members available</div>
                )}
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button data-testid="handoff-confirm-btn" onClick={submit} disabled={saving || !sel}>
            {saving ? "Assigning…" : alreadyHandedOff ? "Reassign" : "Hand off"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
